import { getLog } from '@eigtech/ui-shared-logging'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { Button } from '@eigtech/ui/components/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@eigtech/ui/components/tooltip'
import { cn } from '@eigtech/ui/lib/utils'
import { FileIcon, Info, PlusIcon, TriangleAlert, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { formatBytes, useFileUpload } from '../hooks/use-file-upload'
import { FileEntry } from '../types/file-entry'

const log = getLog()

export interface CompactFileUploadProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'ref'
> {
  ref?: React.Ref<HTMLDivElement>
  files?: FileEntry[]
  maxFiles?: number
  maxSize?: number
  accept?: string
  multiple?: boolean
  className?: string
  disabled?: boolean
  onFilesChange?: (files: FileEntry[]) => void
}

export function CompactFileUpload({
  ref,
  files: filesProp,
  maxFiles = 3,
  maxSize = 2 * 1024 * 1024, // 2MB
  accept = 'image/*',
  multiple = true,
  className,
  disabled,
  onFilesChange,
  ...props
}: CompactFileUploadProps) {
  const [
    { files, isDragging, errors },
    {
      removeFile,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      getInputProps,
    },
  ] = useFileUpload({
    files: filesProp,
    disabled,
    maxFiles,
    maxSize,
    accept,
    multiple,
    onFilesChange,
  })

  return (
    <div ref={ref} className={cn('w-full', className)}>
      {/* Compact Upload Area */}
      <div
        className={cn(
          'border-border flex items-center gap-3 rounded-lg border border-dashed px-3 py-2 transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input {...getInputProps(props)} className="sr-only" />

        {/* Upload Button */}
        <Button
          className={cn(isDragging && 'animate-bounce')}
          disabled={disabled}
          size="sm"
          onClick={openFileDialog}
        >
          <PlusIcon className="h-4 w-4" />
          {multiple ? 'Add files' : 'Select file'}
        </Button>

        {/* File Previews */}
        <div className="flex min-w-0 items-center gap-2">
          {files.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Drop files here or click to browse (max {maxFiles} files)
            </p>
          ) : (
            files.map((fileItem) => (
              <Tooltip key={fileItem.id}>
                <TooltipTrigger
                  className="group flex min-w-0 flex-1 items-center gap-2"
                  render={<div />}
                >
                  {/* File Preview */}
                  <div className="relative">
                    <FilePreview fileItem={fileItem} />

                    {/* Remove Button */}
                    <Button
                      className="border-background absolute -right-2 -top-2 size-5 rounded-full border-2 opacity-0 transition-opacity group-hover:opacity-100"
                      disabled={disabled}
                      mode="icon"
                      variant="destructive"
                      onClick={() => removeFile(fileItem.id)}
                    >
                      <XIcon className="size-3" />
                    </Button>

                    <span className="border-background bg-accent absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full border-2">
                      <Info className="size-4" />
                    </span>
                  </div>

                  {!multiple && <div className="truncate">{fileItem.name}</div>}
                </TooltipTrigger>

                <TooltipContent>
                  <p>{fileItem.name}</p>
                </TooltipContent>
              </Tooltip>
            ))
          )}
        </div>

        {/* File Count */}
        {files.length > 0 && (
          <div className="text-muted-foreground shrink-0 text-xs">
            {files.length}/{maxFiles}
          </div>
        )}
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert appearance="light" className="mt-5" variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>File upload error(s)</AlertTitle>
            <AlertDescription>
              {errors.map((error, index) => (
                <p key={index} className="last:mb-0">
                  {error}
                </p>
              ))}
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}
    </div>
  )
}

function FilePreview({ fileItem }: { fileItem: FileEntry }) {
  const [thumbnail, setThumbnail] = useState<string | null>(null)

  const hasRun = useRef(false)
  const previousFileItemIdRef = useRef(fileItem?.id)

  useEffect(() => {
    const previousFileItemId = previousFileItemIdRef.current
    previousFileItemIdRef.current = fileItem?.id

    if (previousFileItemId !== previousFileItemIdRef.current) {
      hasRun.current = false
      setThumbnail(null)
    }

    if (hasRun.current || fileItem.status !== 'pending') return

    hasRun.current = true

    let ignore = false

    fileItem
      .getThumbnail()
      .then((thumb) => {
        if (!ignore) setThumbnail(thumb)
      })
      .catch((error) => {
        log.error('error getting thumbnail', { error })
      })

    return () => {
      ignore = true
    }
  }, [fileItem])

  return fileItem.isImage && thumbnail ? (
    <img
      alt={fileItem.name}
      className="h-12 w-12 rounded-lg border object-cover"
      src={thumbnail}
      title={`${fileItem.name} (${formatBytes(fileItem.size)})`}
    />
  ) : (
    <div
      className="bg-muted flex h-12 w-12 items-center justify-center rounded-lg border"
      title={`${fileItem.name} (${formatBytes(fileItem.size)})`}
    >
      <FileIcon className="text-muted-foreground h-5 w-5" />
    </div>
  )
}
